import { motion } from "framer-motion";
import { Globe2, Compass, Factory, Handshake, Briefcase, Ship, ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    icon: Globe2,
    title: "International Trade Consulting",
    text: "End-to-end guidance on cross-border trade — from feasibility and regulations to documentation and execution.",
  },
  {
    icon: Compass,
    title: "Market-Entry Strategy",
    text: "Data-informed roadmaps for entering new markets: positioning, pricing, channels, and local requirements.",
  },
  {
    icon: Factory,
    title: "Buyer & Supplier Sourcing",
    text: "Identification and vetting of reliable buyers and suppliers aligned to your quality, volume, and compliance needs.",
  },
  {
    icon: Handshake,
    title: "Business Partnerships",
    text: "Structured matchmaking and negotiation support for joint ventures, distribution, and strategic alliances.",
  },
  {
    icon: Briefcase,
    title: "Trade Representation",
    text: "On-ground representation of your interests in target markets — meetings, follow-ups, and relationship management.",
  },
  {
    icon: Ship,
    title: "Import-Export Advisory",
    text: "Practical advisory on logistics, customs, Incoterms, and trade finance so goods move without friction.",
  },
];

export const Services = () => (
  <section id="services" data-testid="services-section" className="relative bg-royal py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="flex flex-col justify-between gap-6 md:flex-row md:items-end"
      >
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold sm:text-xs">What we do</p>
          <h2 className="mt-6 max-w-xl font-serif text-3xl font-medium leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
            Services built for cross-border growth.
          </h2>
        </div>
        <p className="max-w-sm text-sm leading-relaxed text-slate-400">
          Six focused capabilities that carry a business from first market scan to executed international deals.
        </p>
      </motion.div>

      <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map(({ icon: Icon, title, text }, i) => (
          <motion.div
            key={title}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ delay: (i % 3) * 0.1, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            data-testid={`service-card-${i + 1}`}
            className="group relative overflow-hidden rounded-xl border border-white/10 bg-royal-card/60 p-8 transition-colors duration-500 hover:border-gold/50"
          >
            <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-gold/0 blur-3xl transition-colors duration-700 group-hover:bg-gold/15" />
            <div className="flex items-start justify-between">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 text-gold transition-colors duration-500 group-hover:bg-gold group-hover:text-royal-deep">
                <Icon size={20} />
              </div>
              <span className="font-mono text-xs text-slate-500">0{i + 1}</span>
            </div>
            <h3 className="mt-8 font-serif text-xl font-semibold text-white sm:text-2xl">{title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">{text}</p>
            <ArrowUpRight
              size={18}
              className="mt-6 text-gold opacity-0 transition-all duration-500 group-hover:translate-x-1 group-hover:opacity-100"
            />
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
